import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';

const ERROR_MESSAGE =
  'ERROR in PixInputCode component, you must provide an @ariaLabel and a @legend';
const NAVIGATION_EXPLANATION =
  'Pour se déplacer de champ en champ vous pouvez utiliser la tabulation ou bien les flèches gauche et droite de votre clavier.';
const NUMBER_INPUT_EXPLANATION =
  'Pour remplir un champ vous pouvez utiliser des chiffres de 1 à 9 ou bien les flèches haut et bas de votre clavier pour incrémenter de 1 la valeur du champ.';
const TEXT_INPUT_EXPLANATION =
  'Pour remplir un champ vous pouvez utiliser les caractères alphanumériques de votre clavier.';

/**
 * @typedef {object} PixInputCodeArgs
 * @property {string} ariaLabel - Nom de chaque champ pour les lecteurs d'écran, suivi de son numéro. Obligatoire.
 * @property {string} legend - Intitulé du groupe de champs. Obligatoire.
 * @property {number} [numInputs] - Nombre de champs à saisir. Par défaut : `6`.
 * @property {'number' | 'text'} [inputType] - Nature du code attendu : `number` n'accepte que les chiffres de 1 à 9, `text` accepte les caractères alphanumériques. Par défaut : `number`.
 * @property {string} [explanationOfUse] - Remplace le texte qui explique aux lecteurs d'écran comment naviguer et saisir le code.
 * @property {(code: string) => unknown} [onAllInputsFilled] - Appelée avec le code complet dès que tous les champs sont remplis.
 */

/**
 * @typedef {object} PixInputCodeSignature
 * @property {HTMLInputElement} Element
 * @property {PixInputCodeArgs} Args
 * @property {{}} Blocks
 */

export default class PixInputCode extends Component {
  moveFocus = true;
  numInputs = this.args.numInputs || 6;

  get numberOfIterations() {
    return Array.from({ length: this.numInputs }, (_, i) => i + 1);
  }

  get ariaLabel() {
    if (!this.args.ariaLabel) {
      throw new Error(ERROR_MESSAGE);
    }
    return this.args.ariaLabel;
  }

  get explanationOfUse() {
    const defaultFillingExplanation =
      this.inputType === 'number' ? NUMBER_INPUT_EXPLANATION : TEXT_INPUT_EXPLANATION;
    const defaultExplanationOfUseMessage = NAVIGATION_EXPLANATION + ' ' + defaultFillingExplanation;

    return this.args.explanationOfUse ? this.args.explanationOfUse : defaultExplanationOfUseMessage;
  }

  get legend() {
    if (!this.args.legend) {
      throw new Error(ERROR_MESSAGE);
    }
    return this.args.legend;
  }

  get inputType() {
    return this.args.inputType || 'number';
  }

  get placeholder() {
    return this.inputType === 'number' ? '0' : null;
  }

  focusElement(index) {
    const nextElem = document.getElementById(`code-input-${index}`);
    nextElem && nextElem.focus();
  }

  validateInput(elem) {
    if (this.inputType === 'number') {
      const startsWithDigit = elem.value.match(/^[1-9]/);
      if (!startsWithDigit) elem.value = '';
    }
    if (elem.value.length > 1) elem.value = [...elem.value][0];
  }

  triggerAction() {
    if (!this.args.onAllInputsFilled) return;

    const code = [];
    for (let i = 1; i <= this.numInputs; i++) {
      const elem = document.getElementById(`code-input-${i}`);
      elem.value.length > 0 && code.push(elem.value);
    }
    if (code.length === this.numInputs) {
      this.args.onAllInputsFilled(code.join(''));
    }
  }

  @action
  handleCodeInput(index) {
    const elem = document.getElementById(`code-input-${index}`);
    this.validateInput(elem);
    if (elem.value.length > 0) {
      elem.classList.add('filled');
      this.moveFocus && this.focusElement(index + 1);
      this.triggerAction();
    } else {
      elem.classList.remove('filled');
    }
  }

  @action
  handleKeyUp(index, event) {
    const eventMap = {
      Backspace: index - 1,
      ArrowLeft: index - 1,
      ArrowRight: index + 1,
    };
    const newIndex = eventMap[event.code || event.key];
    this.focusElement(newIndex);
  }

  @action
  handleArrowUpOrDown(event) {
    this.moveFocus = true;
    const eventCode = event.code || event.key;
    if (['ArrowUp', 'ArrowDown'].includes(eventCode)) this.moveFocus = false;
  }

  @action
  handlePaste(index, event) {
    event.preventDefault();
    event.stopPropagation();

    const pastedText = (event.clipboardData || window.clipboardData).getData('text');
    const pastedTextWithOnlyValidCharacters = _extractValidCharacters(pastedText);

    pastedTextWithOnlyValidCharacters.forEach((char) => {
      const input = document.getElementById(`code-input-${index}`);
      if (input) {
        this.focusElement(index);
        input.value = char;
        input.classList.add('filled');
        index++;
      }
    });
    this.focusElement(index);
    this.triggerAction();
  }

  @action
  handleFocus(index) {
    const element = document.getElementById(`code-input-${index}`);
    element && element.select();
  }

  <template>
    <div class="pix-input-code">
      <fieldset aria-describedby="pix-input-code__details-of-use">
        <p id="pix-input-code__details-of-use">{{this.explanationOfUse}}</p>
        <legend>{{this.legend}}</legend>
        {{#each this.numberOfIterations as |i|}}
          <input
            id="code-input-{{i}}"
            type="{{this.inputType}}"
            aria-label="{{this.ariaLabel}} {{i}}"
            class="pix-input-code__input"
            min="1"
            max="9"
            autocomplete="off"
            placeholder="{{this.placeholder}}"
            {{on "keydown" this.handleArrowUpOrDown}}
            {{on "keyup" (fn this.handleKeyUp i)}}
            {{on "input" (fn this.handleCodeInput i)}}
            {{on "paste" (fn this.handlePaste i)}}
            {{on "focus" (fn this.handleFocus i)}}
            ...attributes
          />
        {{/each}}
      </fieldset>
    </div>
  </template>
}

function _extractValidCharacters(pastedText) {
  const alphanumericCharacters = /^[a-zA-Z0-9_]*$/;
  return [...pastedText].filter((char) => alphanumericCharacters.test(char));
}
